import { create } from "zustand";
import { queryClient } from "../lib/queryClient";

const TOKEN_KEY = "valoriza_token";
const USER_KEY = "valoriza_user";
const SESSION_ID_KEY = "valoriza_session_id";
export const SESSION_EXPIRY_KEY  = "valoriza_login_time";
export const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 horas

function loadFromStorage() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = JSON.parse(localStorage.getItem(USER_KEY) || "null");
    const sessionId = localStorage.getItem(SESSION_ID_KEY) || null;
    if (token && user) return { token, user, sessionId };
  } catch (_) {}
  return { token: null, user: null, sessionId: null };
}

const { token: storedToken, user: storedUser, sessionId: storedSessionId } = loadFromStorage();

export const useUserStore = create((set) => ({
  user: storedUser,
  token: storedToken,
  sessionId: storedSessionId,
  isAuthenticated: !!(storedToken && storedUser),
  isDemoMode: false,

  // Called after local email/password login (server returns JWT)
  loginWithCredentials: (data) => {
    const user = {
      id: data.id,
      email: data.email,
      display_name: data.display_name,
      role: data.role,
      sedes: data.sedes ?? null,
    };
    const sessionId = data.session_id || null;
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now()));
    if (sessionId) localStorage.setItem(SESSION_ID_KEY, sessionId);
    set({ user, token: data.token, sessionId, isAuthenticated: true });
  },

  // Called after MSAL login (server validates MSAL token and returns local JWT)
  loginWithMsal: (data) => {
    const user = {
      id: data.email,
      email: data.email,
      display_name: data.display_name,
      role: data.role,
      sedes: data.sedes ?? null,
    };
    const sessionId = data.session_id || null;
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now()));
    if (sessionId) localStorage.setItem(SESSION_ID_KEY, sessionId);
    set({ user, token: data.token, sessionId, isAuthenticated: true });
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SESSION_EXPIRY_KEY);
    localStorage.removeItem(SESSION_ID_KEY);
    // Clear React Query cache so the next user/session gets fresh data
    try { queryClient.clear(); } catch (_) {}
    set({ user: null, token: null, sessionId: null, isAuthenticated: false, isDemoMode: false });
  },
}));

// ─── useRole hook ─────────────────────────────────────────────────────────────
export function useRole() {
  const { user } = useUserStore();
  const role = user?.role || "viewer";
  return {
    role,
    isSuperAdmin: role === "superadmin",
    isAdmin: role === "superadmin",
    canExport: true,  // All authenticated users can export their filtered data
    canManageUsers: role === "superadmin",
  };
}
